import { Close } from "@mui/icons-material";
import { Box, Stack, TextField, TextFieldProps } from "@mui/material";
import { ChangeEvent, forwardRef, useEffect, useRef, useState } from "react";

type DebounceProps = {
  handleDebounce: (value: string) => void;
  debounceTimeout: number;
  disableClearable?: boolean;
  value?: string;
};

const DebounceInput = forwardRef<
  HTMLInputElement,
  TextFieldProps & DebounceProps
>((props, ref) => {
  const {
    handleDebounce,
    disableClearable,
    debounceTimeout,
    value = "",
    ...rest
  } = props;

  const [val, setVal] = useState("");

  const timerRef = useRef<number | null>(null);

  const searchWrapperRef = useRef<HTMLDivElement | null>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setVal(event.target.value);
    timerRef.current = window.setTimeout(() => {
      handleDebounce(event.target.value);
    }, debounceTimeout);
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "f") {
        event.preventDefault();

        const searchInput =
          searchWrapperRef.current?.querySelector("#search-input");
        if (searchInput) {
          (searchInput as HTMLInputElement).focus();
        }
      } else if (event.key === "Escape") {
        const searchInput =
          searchWrapperRef.current?.querySelector("#search-input");
        if (searchInput) {
          (searchInput as HTMLInputElement).blur();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  useEffect(() => setVal(value), [value]);

  return (
    <Box
      position={"relative"}
      className="debounced-input-close-icon-wrapper"
      sx={{
        width:'100%',
        "&:focus-within": {
          ".debounced-input-close-icon": {
            zIndex: 1,
            opacity: 1,
            visibility: "visible",
          },
        },
      }}
      ref={searchWrapperRef}
    >
      <TextField
        {...rest}
        inputRef={ref}
        id={"search-input"}
        value={val}
        onChange={handleChange}
      />
      {!disableClearable && val && (
        <Stack
          className={"debounced-input-close-icon"}
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            zIndex: -1,
            height: "calc(100% - 4px)",
            width: "40px",
            borderRadius: "5px",
            opacity: 0,
            visibility: "hidden",
            margin: "2px",
            transition:
              "0.2s z-index ease-in-out, 0.2s opacity ease-in-out,0.2s visibility ease-in-out",
            cursor: "pointer",
            backgroundColor: "transparent",
            backgroundImage:
              "linear-gradient(to right, transparent 0%, white 20%)",
          }}
          justifyContent={"center"}
          alignItems={"center"}
          onClick={() => {
            handleDebounce("");
            setVal("");
          }}
        >
          <Close color="primary" />
        </Stack>
      )}
    </Box>
  );
});
export default DebounceInput;
